// =============================================================================
// features/sales/campaigns/wizard/WizardContent.tsx — Orquestrador das 6
// etapas (F35-S08, doc 26 §6.6). Estado do formulário inteiro vive aqui
// (useState) — nunca é descartado ao navegar entre etapas nem por um erro de
// rede (DoD "voltar não perde o preenchido" / "erro não descarta o
// formulário"). URL (`useWizardStepUrl`) e rehidratação
// (`useWizardHydration`) ficam em hooks próprios para manter este arquivo
// abaixo de 200 linhas (norma anti-padrão do DS).
// =============================================================================

import * as React from 'react';

import { Button } from '../../../../components/ui/Button';
import { useToast } from '../../../../components/ui/Toast';
import {
  useCreateCampaignDraft,
  usePatchCampaignDraft,
} from '../../../../hooks/sales/useCampaignWizard';
import type { CampaignPatchBody } from '../../../../hooks/sales/useCampaignWizard';

import { WizardStepper } from './components/WizardStepper';
import { Step1Objective } from './steps/Step1Objective';
import { Step2Audience } from './steps/Step2Audience';
import { Step3Hygiene } from './steps/Step3Hygiene';
import { Step4Distribution } from './steps/Step4Distribution';
import { Step5Cadence } from './steps/Step5Cadence';
import { Step6Review } from './steps/Step6Review';
import { EMPTY_AUDIENCE, EMPTY_CADENCE, EMPTY_DISTRIBUTION, EMPTY_OBJECTIVE } from './types';
import type {
  AudienceState,
  CadenceValues,
  DistributionValues,
  HygieneResultState,
  ObjectiveValues,
} from './types';
import { useWizardHydration } from './useWizardHydration';
import { useWizardStepUrl } from './useWizardStepUrl';

export function WizardContent(): React.JSX.Element {
  const { toast } = useToast();
  const { campaignId, setCampaignId, step, furthestReached, goToStep, resetToStart } =
    useWizardStepUrl();

  const [objective, setObjective] = React.useState<ObjectiveValues>(EMPTY_OBJECTIVE);
  const [audience, setAudience] = React.useState<AudienceState>(EMPTY_AUDIENCE);
  const [hygiene, setHygiene] = React.useState<HygieneResultState | null>(null);
  const [distribution, setDistribution] = React.useState<DistributionValues>(EMPTY_DISTRIBUTION);
  const [cadence, setCadence] = React.useState<CadenceValues>(EMPTY_CADENCE);
  const [stepError, setStepError] = React.useState<string | null>(null);

  const { notFound } = useWizardHydration(campaignId, setObjective, setDistribution, setCadence);

  React.useEffect(() => {
    if (notFound) {
      toast('Não encontramos esse rascunho — começando uma campanha nova.', 'info');
      resetToStart();
    }
    // toast/resetToStart são estáveis entre renders — só reage a `notFound`.
  }, [notFound]);

  const { mutateAsync: createDraft, isPending: isCreating } = useCreateCampaignDraft();
  const { mutateAsync: patchDraft, isPending: isPatching } = usePatchCampaignDraft();

  async function patchStep(body: CampaignPatchBody, next: 4 | 5 | 6): Promise<void> {
    if (!campaignId) return;
    setStepError(null);
    try {
      await patchDraft({ id: campaignId, body });
      goToStep(next);
    } catch (err) {
      setStepError(err instanceof Error ? err.message : 'Não foi possível salvar esta etapa.');
    }
  }

  async function handleStep1Continue(): Promise<void> {
    setStepError(null);
    try {
      let id = campaignId;
      if (id) {
        await patchDraft({
          id,
          body: {
            name: objective.name.trim(),
            description: objective.description.trim() || null,
            productId: objective.productId || null,
            priority: objective.priority,
            ...(objective.ownerUserId ? { ownerUserId: objective.ownerUserId } : {}),
          },
        });
      } else {
        const created = await createDraft({
          cityId: objective.cityId,
          name: objective.name.trim(),
          ...(objective.description.trim() ? { description: objective.description.trim() } : {}),
          ...(objective.productId ? { productId: objective.productId } : {}),
          ...(objective.ownerUserId ? { ownerUserId: objective.ownerUserId } : {}),
          priority: objective.priority,
        });
        id = created.id;
        setCampaignId(id);
      }
      goToStep(2, id);
    } catch (err) {
      setStepError(
        err instanceof Error ? err.message : 'Não foi possível salvar o objetivo da campanha.',
      );
    }
  }

  return (
    <div className="flex flex-col gap-6 max-w-3xl mx-auto">
      <div className="flex flex-col gap-2">
        <h1
          className="font-display font-bold text-ink"
          style={{ fontSize: 'var(--text-2xl)', letterSpacing: '-0.04em', lineHeight: 1.1 }}
        >
          Nova campanha
        </h1>
        <p className="font-sans text-sm text-ink-3">
          Salvar não ativa nada — a ativação é uma confirmação separada na última etapa.
        </p>
      </div>

      <WizardStepper
        current={step}
        furthestReached={furthestReached}
        onNavigate={(s) => goToStep(s)}
      />

      {step === 1 && (
        <Step1Objective
          values={objective}
          onChange={setObjective}
          onContinue={() => void handleStep1Continue()}
          isPending={isCreating || isPatching}
          errorMessage={stepError}
        />
      )}

      {step === 2 && campaignId && (
        <Step2Audience
          values={audience}
          onChange={setAudience}
          onBack={() => goToStep(1)}
          onContinue={() => goToStep(3)}
        />
      )}

      {step === 3 && campaignId && (
        <Step3Hygiene
          campaignId={campaignId}
          audience={audience}
          onBack={() => goToStep(2)}
          onContinue={(result) => {
            setHygiene(result);
            goToStep(4);
          }}
        />
      )}

      {step === 4 && (
        <Step4Distribution
          values={distribution}
          onChange={setDistribution}
          onBack={() => goToStep(3)}
          onContinue={() =>
            void patchStep({ assignmentStrategy: distribution.assignmentStrategy }, 5)
          }
          isPending={isPatching}
          errorMessage={stepError}
        />
      )}

      {step === 5 && (
        <Step5Cadence
          values={cadence}
          onChange={setCadence}
          onBack={() => goToStep(4)}
          onContinue={() =>
            void patchStep(
              {
                weeklyWindow: cadence.weeklyWindow,
                timezone: cadence.timezone,
                maxAttempts: cadence.maxAttempts,
                minIntervalMinutes: cadence.minIntervalMinutes,
              },
              6,
            )
          }
          isPending={isPatching}
          errorMessage={stepError}
        />
      )}

      {step === 6 && campaignId && (
        <Step6Review
          campaignId={campaignId}
          objective={objective}
          hygiene={hygiene}
          distribution={distribution}
          cadence={cadence}
          onBack={() => goToStep(5)}
        />
      )}

      {step > 1 && !campaignId && (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <p className="font-sans text-sm text-ink-3">
            Esta etapa depende do objetivo salvo — volte e preencha o nome e a cidade primeiro.
          </p>
          <Button variant="outline" size="sm" onClick={() => goToStep(1)}>
            Voltar ao objetivo
          </Button>
        </div>
      )}
    </div>
  );
}
